import { useEffect, useMemo } from 'react';
import { useGetContentFreshnessRuleQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import { RuleRequest, RuleResponse } from 'store/api/apiTypes';

/**
 * useContentFreshnessRule
 * Fetches Content Freshness visibility rule for the selected project domain.
 * Mirrors statistical evidence & conversational writing rule hook patterns.
 */
export const useContentFreshnessRule = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );
  const activeReport = useAppSelector(state => state.history.activeReport);
  const queryArgs = useMemo<RuleRequest | null>(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const {
    data: rule,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetContentFreshnessRuleQuery(queryArgs as RuleRequest, {
    skip: !queryArgs,
    refetchOnMountOrArgChange: true,
  });

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  useEffect(() => {
    if (error) {
      handleApiError(error as any);
    }
  }, [error, handleApiError]);

  usePollingOnError(queryArgs, error, refetch, rule);

  const typed = rule as RuleResponse | undefined;

  return {
    contentFreshness: typed,
    company: typed?.company,
    products: typed?.products || {},
    isLoading,
    isFetching,
    error,
    refetchContentFreshness: refetch,
    showContentFreshnessErrorModal: showErrorModal,
    handleContentFreshnessRetry: handleRetry,
    hideContentFreshnessErrorModal: hideErrorModal,
    hasContentFreshness: !!typed,
    queryArgs,
  };
};
